import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketMessage } from '@/hooks/socket/useSocket';
import type { WsServerMessage } from '@/lib/types';
import { invalidateOrgPluginInstallationQueries } from './invalidateOrgPluginInstallationQueries';

/**
 * Subscribes the React Query cache to the server's
 * `org:plugin_installation_changed` broadcasts. Every event invalidates
 * the org plugins list, per-plugin detail/preview rows, and the plugin
 * manifest (`queryKeys.plugins`) so newly installed plugins contribute
 * `routes`/`navItems` (and uninstalled ones stop contributing) without a
 * full page reload — see `invalidateOrgPluginInstallationQueries`.
 *
 * Mounted once in `AppShell` so install/uninstall updates reach the
 * sidebar and `/integrations` even when the user has navigated away from
 * the plugins pages. Drops events for other organizations on the floor —
 * the active-org filter is the only scoping we need.
 */
export function useOrgPluginInstallationSocket(organizationId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'org:plugin_installation_changed') return;
        if (!organizationId || msg.organizationId !== organizationId) return;
        const names = msg.cascade && msg.cascade.length > 0 ? msg.cascade : [msg.pluginName];
        invalidateOrgPluginInstallationQueries(queryClient, organizationId, names);
      },
      [organizationId, queryClient],
    ),
  );
}
